import { Loader2 } from "lucide-react"

import { StepHeader, WizardLayout } from "@/components/agents/setup/shell"
import { FormField } from "@/components/form-field"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

import { PRESET_COLORS } from "./appearance-step"
import { InstallSnippet, normalizeDomain } from "./install-snippet"
import type { ChatbotSetupStepProps } from "./types"

export function InstallStep({
  stepNumber,
  totalSteps,
  data,
  onUpdate,
  onNext,
  onBack,
  processing,
  agentUid,
  brandColor,
}: ChatbotSetupStepProps & { agentUid: string; brandColor: string | null }) {
  // The scraped brand color leads (when detected, IMP-458), then the same
  // presets the edit drawer offers.
  const swatches = [
    ...(brandColor ? [brandColor] : []),
    ...PRESET_COLORS.filter(
      (c) => c.toLowerCase() !== brandColor?.toLowerCase(),
    ),
  ]

  return (
    <WizardLayout
      footer={
        <>
          <Button variant="outline" onClick={onBack} disabled={processing}>
            Back
          </Button>
          <Button onClick={onNext} disabled={processing}>
            {processing && <Loader2 className="size-4 animate-spin" />}
            {processing ? "Launching…" : "Launch"}
          </Button>
        </>
      }
    >
      <StepHeader
        eyebrow={`Step ${stepNumber} of ${totalSteps}`}
        title="Add the widget to your site"
        description="Paste the snippet into your site's code. The widget goes live as soon as it's added."
      />

      <FormField label="Color">
        <div className="flex items-center gap-2">
          {swatches.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onUpdate({ primary_color: c })}
              aria-label={c}
              className={cn(
                "size-7 rounded-full border-2 transition-transform",
                data.primary_color.toLowerCase() === c.toLowerCase()
                  ? "border-foreground scale-110"
                  : "border-transparent",
              )}
              style={{ backgroundColor: c }}
            />
          ))}
          <label className="relative">
            <input
              type="color"
              value={data.primary_color}
              onChange={(e) => onUpdate({ primary_color: e.target.value })}
              className="absolute inset-0 cursor-pointer opacity-0"
            />
            <span className="border-border text-muted-foreground hover:bg-muted/30 flex size-7 items-center justify-center rounded-full border text-xs transition-colors">
              +
            </span>
          </label>
        </div>
      </FormField>

      <FormField label="Embed domain" htmlFor="embed-domain">
        <Input
          id="embed-domain"
          value={data.embed_domain}
          onChange={(e) => onUpdate({ embed_domain: e.target.value })}
          onBlur={(e) =>
            onUpdate({ embed_domain: normalizeDomain(e.target.value) })
          }
          placeholder="yoursite.com"
        />
        {/* Helper, not a sublabel — it explains a consequence of the value
            you enter, so it reads under the input. */}
        <p className="text-muted-foreground mt-2 text-xs">
          For security, the widget only works on this domain.
        </p>
      </FormField>

      <InstallSnippet agentUid={agentUid} />
    </WizardLayout>
  )
}
